import { useEffect, useRef, useState } from "react";

import { useQuery, useQueryClient } from "@tanstack/react-query";

import { getFieldShape, getFieldShapeAdmin, getFieldShapes } from "services";

import { getEnvironment, getLocalKlimVar } from "utils";

import { DEFAULT_CACHE_TIME, GET_FIELD_SHAPE, GET_FIELD_SHAPES } from "consts";

import { FieldListField } from "models";

export const useGetFieldShape = ({
  type,
  fieldId,
  highQuality = false,
  enabled = true,
}: {
  type?: string;
  fieldId: string;
  highQuality?: boolean;
  enabled?: boolean;
}) => {
  const farmId = getLocalKlimVar("farmId");

  const isAdmin = getEnvironment().admin;

  const { data, isLoading } = useQuery({
    queryKey: [GET_FIELD_SHAPE, fieldId, type, isAdmin, highQuality],
    queryFn: () => (isAdmin ? getFieldShapeAdmin({ fieldId, type }) : getFieldShape({ fieldId, type, highQuality })),
    enabled: !!fieldId && !!farmId && enabled,
    gcTime: DEFAULT_CACHE_TIME * 7, // 7 days stale time
    staleTime: DEFAULT_CACHE_TIME * 7, // 7 days stale time
  });

  return {
    fieldShape: data?.fieldShape,
    fieldShapeLoading: isLoading,
  };
};

export const useGetFieldShapes = ({
  enabled = true,
  fieldCount,
  refetchOnFirstCount = true,
}: {
  enabled?: boolean;
  fieldCount?: number;
  /** false: the first count only refetches shapes that no longer match the fields, for a page that just shows the outlines */
  refetchOnFirstCount?: boolean;
} = {}) => {
  const farmId = getLocalKlimVar("farmId");
  const queryClient = useQueryClient();
  const prevFieldCountRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (fieldCount === undefined) return;
    const previousFieldCount = prevFieldCountRef.current;
    prevFieldCountRef.current = fieldCount;
    if (previousFieldCount === fieldCount) return;
    // the fields page refreshes the shapes on every visit; the home map keeps cached shapes that still match the fields,
    // so the landing page does not refetch shapes that are fresh for days (an upload elsewhere changes the count)
    if (previousFieldCount === undefined && !refetchOnFirstCount) {
      const cached = queryClient.getQueryData<{ fieldShapes: unknown[] }>([GET_FIELD_SHAPES, farmId]);
      if (cached?.fieldShapes.length === fieldCount) return;
    }
    queryClient.refetchQueries({ queryKey: [GET_FIELD_SHAPES] });
  }, [fieldCount, farmId, queryClient, refetchOnFirstCount]);

  const { data, isLoading } = useQuery({
    queryKey: [GET_FIELD_SHAPES, farmId],
    queryFn: ({ signal }) => getFieldShapes({ signal }),
    enabled: !!farmId && enabled,
    gcTime: DEFAULT_CACHE_TIME * 7,
    staleTime: DEFAULT_CACHE_TIME * 7,
  });

  return {
    fieldShapes: data?.fieldShapes ?? [],
    fieldShapesLoading: isLoading,
  };
};

export const useSelectedField = (fields: FieldListField[] | undefined) => {
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!selectedFieldId) return;
    if (!fields?.some((f) => f.id === selectedFieldId)) {
      setSelectedFieldId(null);
      queryClient.invalidateQueries({ queryKey: [GET_FIELD_SHAPES] });
    }
  }, [fields, selectedFieldId, queryClient]);

  return { selectedFieldId, setSelectedFieldId };
};
